import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CaseEvent, ClosedReason, DuplicateGroup, DupReviewStatus } from '@taksikortti/shared';
import { api } from './client';

export interface DuplicatesListParams {
  status?: DupReviewStatus | null;
  page?: number;
  pageSize?: number;
}

export interface DuplicatesListResponse {
  rows: DuplicateGroup[];
  page: number;
  pageSize: number;
  total: number;
}

function toQuery(p: DuplicatesListParams): string {
  const u = new URLSearchParams();
  if (p.status) u.set('status', p.status);
  if (p.page != null) u.set('page', String(p.page));
  if (p.pageSize != null) u.set('pageSize', String(p.pageSize));
  const s = u.toString();
  return s ? `?${s}` : '';
}

export function useDuplicatesList(params: DuplicatesListParams) {
  return useQuery({
    queryKey: ['duplicates', 'list', params],
    queryFn: () => api<DuplicatesListResponse>(`/duplicates${toQuery(params)}`),
    staleTime: 30_000,
  });
}

export interface DuplicateDetailResponse {
  group: DuplicateGroup;
  timeline: CaseEvent[];
}

export function useDuplicateDetail(korttinumero: string, groupId: string) {
  return useQuery({
    queryKey: ['duplicates', 'detail', korttinumero, groupId],
    queryFn: () => api<DuplicateDetailResponse>(
      `/duplicates/${encodeURIComponent(korttinumero)}/${encodeURIComponent(groupId)}`,
    ),
    staleTime: 15_000,
  });
}

// Unified case-ladder actions. The API also supports dormant 'mark_review' /
// 'reassign' (legacy) — the UI no longer offers them.
export type DuplicateAction =
  | { kind: 'send_query' }
  | { kind: 'send_reminder' }
  | { kind: 'dismiss'; reason?: ClosedReason; comment?: string }
  | { kind: 'reopen' }
  | { kind: 'edit_comment'; comment: string };

export interface ApplyDuplicateActionResponse {
  korttinumero: string;
  groupId: string;
  reviewStatus: DupReviewStatus;
  newEtag: string;
}

export function useApplyDuplicateAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      korttinumero,
      groupId,
      etag,
      action,
    }: {
      korttinumero: string;
      groupId: string;
      etag: string;
      action: DuplicateAction;
    }) =>
      api<ApplyDuplicateActionResponse>(
        `/duplicates/${encodeURIComponent(korttinumero)}/${encodeURIComponent(groupId)}/actions`,
        { method: 'POST', body: { etag, action } },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['duplicates'] });
      qc.invalidateQueries({ queryKey: ['worklist'] });
    },
  });
}
